import type { UserRole } from "@/types/admin";

export const featureKeys = [
  "paintbooth",
  "eupviewer",
  "addoneupcreator",
  "weaponviewer",
  "uvstudio",
  "liverymagic",
  "itemimage",
  "badgemaker",
  "embroiderytool",
  "blinklab",
  "blinklab3d",
  "metaroom",
  "packbench",
  "codesmith",
  "codevisualizer",
  "scriptdocs",
  "tripoai",
  "chevronbuilder",
  "creatorconvert",
  "inklab",
  "handlingdiff",
  "resmon",
  "assets",
] as const;

export type FeatureKey = (typeof featureKeys)[number];

/**
 * Tools released to Studio members first, as an early-access perk, before they
 * roll out to the other plans. These are NOT part of any lower tier's bundle,
 * so a subscriber on another plan can't add them à la carte — the only way in
 * is Studio. The paywall (AccessDenied) and the locked tool cards read this to
 * show a "Studio-only for now" upsell instead of the generic "add it to your
 * plan" copy. When a tool graduates to general availability, drop it here.
 */
export const studioEarlyAccessFeatureKeys: FeatureKey[] = [];

export function isStudioEarlyAccessFeature(
  feature: FeatureKey | null | undefined,
): boolean {
  return feature != null && studioEarlyAccessFeatureKeys.includes(feature);
}

export type AccountStatus = "active" | "locked" | "inactive";

const legacyAllServicesFeatureKeys: FeatureKey[] = [
  "paintbooth",
  "eupviewer",
  "weaponviewer",
  "uvstudio",
  "blinklab",
  "metaroom",
  "packbench",
  "codesmith",
  "codevisualizer",
];

export type AccessContext = {
  userId: string;
  email: string | null;
  role: UserRole;
  accountStatus: AccountStatus;
  isAdmin: boolean;
  permissions: Record<FeatureKey, boolean>;
  profile: Record<string, unknown>;
  currentIp: string | null;
};

export function normalizeManualPermissions(
  value: unknown,
): Partial<Record<FeatureKey, boolean>> {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }

  const normalized: Partial<Record<FeatureKey, boolean>> = {};
  for (const [key, raw] of Object.entries(value as Record<string, unknown>)) {
    if (!featureKeys.includes(key as FeatureKey)) continue;
    if (typeof raw !== "boolean") continue;
    normalized[key as FeatureKey] = raw;
  }
  return normalized;
}

export function buildFeaturePermissions(
  profile: Record<string, unknown>,
  options?: {
    isAdmin?: boolean;
    personalFeatures?: Iterable<FeatureKey>;
  },
): Record<FeatureKey, boolean> {
  const manualPermissions = normalizeManualPermissions(profile.manual_permissions);

  let permissions: Record<FeatureKey, boolean>;

  if (options?.isAdmin) {
    permissions = {
      paintbooth: true,
      eupviewer: true,
      addoneupcreator: true,
      weaponviewer: true,
      uvstudio: true,
      liverymagic: true,
      itemimage: true,
      badgemaker: true,
      embroiderytool: true,
      blinklab: true,
      blinklab3d: true,
      metaroom: true,
      packbench: true,
      codesmith: true,
      codevisualizer: true,
      scriptdocs: true,
      tripoai: true,
      chevronbuilder: true,
      creatorconvert: true,
      inklab: true,
      handlingdiff: true,
      resmon: true,
      assets: true,
    };
  } else {
    const personalFeatures = new Set(options?.personalFeatures ?? []);
    const hasAnyPersonalSubscription = personalFeatures.size > 0;
    const hasLegacyAllServices =
      legacyAllServicesFeatureKeys.every((feature) => personalFeatures.has(feature)) ||
      legacyAllServicesFeatureKeys.every((feature) => manualPermissions[feature] === true);
    permissions = {
      paintbooth: personalFeatures.has("paintbooth"),
      eupviewer: personalFeatures.has("eupviewer"),
      addoneupcreator: personalFeatures.has("eupviewer") || personalFeatures.has("addoneupcreator"),
      weaponviewer: personalFeatures.has("weaponviewer"),
      uvstudio: personalFeatures.has("uvstudio"),
      liverymagic: personalFeatures.has("liverymagic"),
      itemimage: personalFeatures.has("itemimage"),
      badgemaker: personalFeatures.has("badgemaker") || personalFeatures.has("itemimage"),
      embroiderytool: personalFeatures.has("embroiderytool"),
      blinklab: personalFeatures.has("blinklab"),
      blinklab3d: personalFeatures.has("blinklab3d"),
      metaroom: personalFeatures.has("metaroom"),
      packbench: personalFeatures.has("packbench"),
      codesmith: personalFeatures.has("codesmith"),
      codevisualizer: personalFeatures.has("codevisualizer"),
      scriptdocs: personalFeatures.has("scriptdocs") || hasLegacyAllServices,
      tripoai: personalFeatures.has("tripoai") || hasLegacyAllServices,
      chevronbuilder: personalFeatures.has("chevronbuilder") || hasLegacyAllServices,
      creatorconvert: hasAnyPersonalSubscription || hasLegacyAllServices,
      // Ink Lab is admin-gated for now: mapped to NO plan bundle, so no
      // subscriber is granted it here (admins bypass via the branch above,
      // manual_permissions can grant a specific test account). To launch, add
      // "inklab" to a bundle in planLabel.ts + sealSubscriptions.ts.
      inklab: personalFeatures.has("inklab"),
      // Handling Diff (Vehicle Developer and up) and Resmon Report (Server
      // Developer and up) — same `|| hasLegacyAllServices` backfill as
      // chevronbuilder/creatorconvert/scriptdocs/tripoai above, so a
      // grandfathered legacy-all-services subscriber (whose stored
      // personalFeatures set predates both tools) still gets them.
      handlingdiff: personalFeatures.has("handlingdiff") || hasLegacyAllServices,
      resmon: personalFeatures.has("resmon") || hasLegacyAllServices,
      // Asset Directory (vanilla GTA catalogue) — granted on both the Vehicle
      // Developer and Server Developer tracks (see ALL_VEHICLE_DEVELOPER_APPS
      // / ALL_SERVER_DEVELOPER_APPS in sealSubscriptions.ts), same
      // `|| hasLegacyAllServices` backfill as the other dual-bundle features
      // above so a grandfathered legacy-all-services subscriber gets it too.
      assets: personalFeatures.has("assets") || hasLegacyAllServices,
    };
  }

  for (const key of featureKeys) {
    // Live personal subscription access should remain authoritative for tool access.
    // Manual permissions can grant additional access, but imported/stale `false`
    // values should not suppress an active subscription.
    if (manualPermissions[key] === true) {
      permissions[key] = true;
    }
  }

  return permissions;
}

export function deriveAccountStatus(profile: Record<string, unknown>): AccountStatus {
  // Manual, admin-set suspension for a confirmed account sharer. This is the only
  // account-wide lock that can be set today — nothing automated may set it (see
  // sharingPolicy.ts for why: the automated account lock this replaced blocked 70
  // accounts, 43 of them paying, and caught zero sharers).
  if (profile.sharing_suspended_at) {
    return "locked";
  }

  // A configured locked IP is normal account state. Only auto-blocked accounts
  // should be treated as locked for server-side authorization. IP auto-blocking
  // is itself disabled (ipLockPolicy.ts), so this is effectively dormant.
  if (profile.ip_auto_blocked) {
    return "locked";
  }

  return "active";
}

export function hasFeatureAccess(context: AccessContext, feature: FeatureKey) {
  return Boolean(context.permissions[feature]);
}

/**
 * Whether the client should treat this account as a member (subscriber home,
 * member nav, no free-tier limits). A subscription id alone misses accounts
 * an admin granted tools to via manual_permissions — they have no Seal
 * subscription, so they were dropped onto the free home with no way into the
 * tools they were given. Any granted tool counts; free accounts have none.
 */
export function hasMemberAccess(input: {
  isAdmin: boolean;
  billing?: { personalSubscriptionId?: string | null; personalSubscriptionIds?: string[] } | null;
  permissions?: Partial<Record<FeatureKey, boolean>> | null;
}): boolean {
  return (
    input.isAdmin ||
    Boolean(input.billing?.personalSubscriptionId) ||
    (input.billing?.personalSubscriptionIds?.length ?? 0) > 0 ||
    Object.values(input.permissions ?? {}).some(Boolean)
  );
}
